import { useEffect, useRef } from 'react';

import isNil from 'lodash/isNil';

import { dateHelpers } from '@nocowanie/core/helpers';

import { IPageVisitPayload } from '@noc-sm-plugin';

import { ActiveFilterModel, SearchCriteria } from '@app/models';

import { useNocSmPageVisitEvent } from './useNocSmPageVisitEvent';

interface UseNocSmListingSearchPageVisitProps {
    activeFilters: ActiveFilterModel[];
    searchCriteria: SearchCriteria;
}

export const useNocSmListingSearchPageVisit = ({
    activeFilters,
    searchCriteria,
}: UseNocSmListingSearchPageVisitProps) => {
    const eventPayload = useRef<IPageVisitPayload | undefined>();
    const completed = useRef<boolean>(false);
    const { pushPageVisitEvent } = useNocSmPageVisitEvent();

    useEffect(() => {
        if (isNil(searchCriteria)) {
            return;
        }

        const adultsCount = searchCriteria?.guests?.adultsCount;
        const childrenCount = searchCriteria?.guests?.childrenCount;

        const guests =
            childrenCount != null
                ? `${adultsCount}_adults, ${childrenCount}_children`
                : `${adultsCount}_adults`;

        const filters = Array.from(
            new Set(activeFilters.map(filter => filter.label).filter(Boolean)),
        ).join(', ');

        eventPayload.current = {
            products: 'listing_search',
            value: 0,
            /**
             * 1) detail[1] - event name ("listing_search")
             * 2) detail[2] - searched location
             * 3) detail[3] - stay dates ("dd-MM-yyyy - dd-MM-yyyy" format)
             * 4) detail[4] - guests ("X_adults, Y_children" format)
             * 5) detail[5] - list of applied filters (comma-separated)
             */
            details: [
                'listing_search',
                searchCriteria?.location ?? '',
                dateHelpers.formatStayDatesForSalesManago(
                    searchCriteria?.checkInDate,
                    searchCriteria?.checkOutDate,
                ),
                guests,
                filters,
            ],
        };
    }, [activeFilters, searchCriteria]);

    /**
     * Send event to NocSmPlugin on component unmount
     */
    useEffect(() => {
        const handler = () => {
            if (!isNil(eventPayload.current) && !completed.current) {
                completed.current = true;
                pushPageVisitEvent(eventPayload.current);
            }
        };

        window.addEventListener('beforeunload', handler);

        return () => {
            window.removeEventListener('beforeunload', handler);
            handler();
        };
    }, []);
};
